import {
    createContext,
    useContext,
    useEffect,
    useMemo,
    useReducer,
    useState,
    type MouseEvent,
    type ReactElement,
    type ReactNode,
} from 'react';
import IconAction from '~/components/new/IconAction';
import { classNames } from '~/utils/styles';
import type { IconNames } from './Icon';
import Icon, { RemixIconNames } from './Icon';

type Variant = 'info' | 'warn' | 'success' | 'danger';

export type ToastService = {
    error: (content: ReactNode, secondsToHide?: number, config?: Partial<ToastProps>) => void;
    info: (content: ReactNode, secondsToHide?: number, config?: Partial<ToastProps>) => void;
    warn: (content: ReactNode, secondsToHide?: number, config?: Partial<ToastProps>) => void;
    success: (content: ReactNode, secondsToHide?: number, config?: Partial<ToastProps>) => void;
};

export const ToastContext = createContext<ToastService>(null!);

export type ToasterProviderProps = {
    children: ReactNode;
};

type ToastConfig = { id: string } & ToastProps;

type ToastStore = {
    subscribe: (listener: () => void) => () => void;
    getConfigs: () => ToastConfig[];
    add: (config: ToastConfig) => void;
    remove: (id: string) => void;
};

function createToastStore(): ToastStore {
    const configs: ToastConfig[] = [];
    const listeners = new Set<() => void>();

    const notify = () => {
        listeners.forEach((listener) => listener());
    };

    return {
        subscribe(listener) {
            listeners.add(listener);
            return () => {
                listeners.delete(listener);
            };
        },
        getConfigs() {
            return configs;
        },
        add(config) {
            configs.push(config);
            notify();
        },
        remove(id) {
            const index = configs.findIndex((t) => t.id === id);
            if (index === -1) {
                return;
            }
            configs.splice(index, 1);
            notify();
        },
    };
}

function pushToast(
    store: ToastStore,
    variant: Variant,
    content: ReactNode,
    config: Partial<ToastProps> = {},
) {
    const id = Math.random().toString(36).substring(2, 9);

    store.add({
        id,
        variant,
        onClose: () => store.remove(id),
        children: content,
        timeout: typeof config.timeout === 'number' ? config.timeout : 10,
        action: config.action ?? null,
    });
}

export function ToastProvider({ children }: ToasterProviderProps) {
    const [store] = useState(createToastStore);

    const toastService = useMemo<ToastService>(
        () => ({
            error(content, secondsToHide = 30, config) {
                pushToast(store, 'danger', content, {
                    ...config,
                    timeout: secondsToHide > 0 ? secondsToHide : 0,
                });
            },
            info(content, secondsToHide = 10, config) {
                pushToast(store, 'info', content, {
                    ...config,
                    timeout: secondsToHide > 0 ? secondsToHide : 0,
                });
            },
            warn(content, secondsToHide = 10, config) {
                pushToast(store, 'warn', content, {
                    ...config,
                    timeout: secondsToHide > 0 ? secondsToHide : 0,
                });
            },
            success(content, secondsToHide = 10, config) {
                pushToast(store, 'success', content, {
                    ...config,
                    timeout: secondsToHide > 0 ? secondsToHide : 0,
                });
            },
        }),
        [store],
    );

    return (
        <ToastContext.Provider value={toastService}>
            {children}
            <ToastList store={store} />
        </ToastContext.Provider>
    );
}

function ToastList({ store }: { store: ToastStore }) {
    const [, bump] = useReducer((n: number) => n + 1, 0);

    useEffect(() => store.subscribe(bump), [store]);

    return (
        <div
            className='toast-container'
            data-test-component='ui/ToastContainer'>
            {store.getConfigs().map(({ id, ...props }) => (
                <Toast
                    key={id}
                    {...props}
                />
            ))}
        </div>
    );
}

const VARIANT_ICONS: Record<Variant, IconNames> = {
    info: RemixIconNames.RiInformationFill,
    warn: RemixIconNames.RiAlertFill,
    success: RemixIconNames.RiCheckboxCircleFill,
    danger: RemixIconNames.RiForbid2Fill,
};
export type ToastProps = {
    variant: Variant;
    onClose: () => void;
    timeout: number;
    children: ReactNode;
    action: ReactElement<HTMLAnchorElement> | null;
};

function Toast({ variant, onClose, timeout, children, action }: ToastProps) {
    const [exiting, setExiting] = useState(false);
    const handleClose = (e?: MouseEvent<HTMLElement>) => {
        e?.stopPropagation();
        e?.preventDefault();
        setExiting(true);
        setTimeout(() => {
            onClose();
        }, 600); // awaits for exit transition to finish, 0.3ms fade + 0.3ms shrink
    };

    useEffect(() => {
        let tId: NodeJS.Timeout;

        if (timeout > 0) {
            tId = setTimeout(() => {
                handleClose();
            }, timeout * 1000);
        }

        return () => {
            if (tId) {
                clearTimeout(tId);
            }
        };
    }, []);

    return (
        <>
            <div
                className={classNames({
                    [`toast toast-${variant} with-transition`]: true,
                    exiting,
                })}
                data-test-component='ui/Toast'>
                <div className='toast-wrapper'>
                    <Icon
                        icon={VARIANT_ICONS[variant]}
                        className='toast-icon'
                    />
                    <div
                        className='toast-content'
                        data-test-name='content'>
                        {children}
                    </div>
                    {action && (
                        <div
                            className='toast-action'
                            data-test-name='action'>
                            {action}
                        </div>
                    )}

                    <IconAction
                        icon={RemixIconNames.RiCloseFill}
                        onClick={handleClose}
                        colorVar='core-contrast'
                        data-test-name='close'
                    />
                </div>
            </div>
        </>
    );
}

export function useToasts() {
    return useContext(ToastContext);
}
